import { useState } from "react";
import { useContractContext } from "@/contexts/ContractContext";
import { PatchSchedule } from "@/services/fhir";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  useTheme,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import { useFormatter } from "@/utils/useFormatter";
import { AttachMoney, SettingsSuggest } from "@mui/icons-material";
import { useSessionContext } from "@/contexts/SessionContext";

interface FluxPayScheduleFixerProps {
  schedules: any[];
  onFixComplete?: () => void;
}

export function FluxPayScheduleFixer({
  schedules,
  onFixComplete,
}: FluxPayScheduleFixerProps) {
  const { currentContract } = useContractContext();
  const { user, access } = useSessionContext();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<{ [key: string]: string }>({});

  // Check if current contract is fluxPayPlan
  const isFluxPayPlan = currentContract?.[0]?.resource?.identifier?.some(
    (id: any) => id.value === "fluxPayPlan"
  );

  if (!isFluxPayPlan) return null;

  // Filter schedules based on permissions
  const isManager =
    access?.roles?.includes("manager") || access?.roles?.includes("owner");

  const allowedSchedules =
    schedules?.filter((entry: any) => {
      const schedule = entry.resource;

      // Filter out inactive schedules
      if (schedule.active === false) return false;

      // If manager/owner, has access to all schedules
      if (isManager) return true;

      // If practitioner, check if they are an actor in the schedule
      return schedule.actor?.some((actor: any) =>
        actor.reference?.includes(user.username)
      );
    }) || [];

  // Filter allowed schedules that need fixing (missing the specific cost extension)
  const schedulesToFix = allowedSchedules.filter((entry: any) => {
    const schedule = entry.resource;
    const hasCostExtension = schedule.extension?.some(
      (ext: any) =>
        ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/service-cost"
    );
    return !hasCostExtension;
  });

  if (schedulesToFix.length === 0) return null;

  const getNumericValue = (val: string) => {
    if (!val) return 0;
    const numbers = val.replace(/\D/g, "");
    return Number(numbers) / 100;
  };

  const handleFix = async () => {
    const allFilled = schedulesToFix.every((entry: any) => {
      const val = values[entry.resource.id];
      const num = getNumericValue(val);
      return val && num > 0;
    });

    if (!allFilled) {
      toast.warning("Por favor, preencha o valor para todas as agendas.");
      return;
    }

    const hasInvalidValue = schedulesToFix.some((entry: any) => {
      const num = getNumericValue(values[entry.resource.id]);
      return num < 5;
    });

    if (hasInvalidValue) {
      toast.warning("O valor mínimo para cada consulta é de R$ 5,00");
      return;
    }

    setLoading(true);
    try {
      const promises = schedulesToFix.map((entry: any) => {
        const schedule = entry.resource;
        const value = getNumericValue(values[schedule.id]);

        const newExtension = {
          url: "https://fluxmed.com.br/fhir/StructureDefinition/service-cost",
          valueDecimal: value,
        };

        let patchOperations: any[] = [];

        if (schedule.extension) {
          patchOperations.push({
            op: "add",
            path: "/extension/-",
            value: newExtension,
          });
        } else {
          patchOperations.push({
            op: "add",
            path: "/extension",
            value: [newExtension],
          });
        }

        return PatchSchedule(schedule.id, patchOperations);
      });

      await Promise.all(promises);
      toast.success("Agendas configuradas com sucesso!");
      if (onFixComplete) onFixComplete();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar configurações.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (id: string, value: string) => {
    const formatted = useFormatter.formatCurrency(value);
    setValues((prev) => ({ ...prev, [id]: formatted }));
  };

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -24,
          left: "calc(50% - 24px)",
          bgcolor: "primary.main",
          color: "white",
          borderRadius: "50%",
          p: 1.5,
          boxShadow: theme.shadows[4],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SettingsSuggest fontSize="medium" />
      </Box>

      <DialogTitle sx={{ pt: 5, textAlign: "center", pb: 1 }}>
        <Typography variant="h5" fontWeight="700" color="text.primary">
          Configuração Necessária
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 4 }}>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 4 }}
        >
          Para continuar utilizando o sistema com seu plano <b>FluxPay</b>, é
          necessário definir o valor de atendimento para as agendas abaixo.
        </Typography>

        <Stack spacing={2}>
          {schedulesToFix.map((entry: any) => {
            const schedule = entry.resource;
            const scheduleName =
              schedule.name ||
              schedule.extension?.find((ext: any) =>
                ext.url.includes("scheduleName")
              )?.valueString ||
              `Agenda ${schedule.id.slice(0, 8)}...`;

            const specialty = schedule.actor?.find((a: any) =>
              a.reference?.includes("HealthcareService")
            )?.display;

            const displayName = specialty
              ? `${scheduleName} - ${specialty}`
              : scheduleName;

            return (
              <Paper
                key={schedule.id}
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderColor: alpha(theme.palette.divider, 0.6),
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <Stack spacing={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                    <Typography variant="subtitle2" fontWeight="600">
                      {displayName}
                    </Typography>
                  </Box>

                  <TextField
                    label="Valor da Consulta"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values[schedule.id] || ""}
                    onChange={(e) =>
                      handleInputChange(schedule.id, e.target.value)
                    }
                    placeholder="0,00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney
                            fontSize="small"
                            sx={{ color: "primary.main" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: "background.paper" }}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2, justifyContent: "center" }}>
        <Button
          onClick={handleFix}
          variant="contained"
          disabled={loading}
          size="large"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            boxShadow: theme.shadows[4],
          }}
        >
          {loading ? "Salvando Configurações..." : "Salvar e Continuar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
